'use client';

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Check, CircleAlert, ShieldX, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import IconAdaptive from '@/components/IconAdaptive';

export type ModalConfirmVariant = 'danger' | 'warning' | 'info';

export interface ModalConfirmProps {
    isOpen: boolean;
    onClose: () => void;
    message: string;
    subMessage?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    variant?: ModalConfirmVariant;
}

const VARIANT_STYLES: Record<
    ModalConfirmVariant,
    {
        icon: LucideIcon;
        iconColor: string;
        confirmColorType: 0 | 1 | 2;
    }
> = {
    danger: {
        icon: ShieldX,
        iconColor: 'text-color-modal-confirm-danger',
        confirmColorType: 0,
    },
    warning: {
        icon: AlertTriangle,
        iconColor: 'text-color-modal-confirm-warning',
        confirmColorType: 0,
    },
    info: {
        icon: CircleAlert,
        iconColor: 'text-color-modal-confirm-info',
        confirmColorType: 0,
    },
};

export default function ModalConfirm({
    isOpen,
    onClose,
    message,
    subMessage,
    confirmLabel,
    cancelLabel,
    onConfirm,
    onCancel,
    variant = 'info',
}: ModalConfirmProps) {
    const [modalContainer, setModalContainer] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setModalContainer(document.body);
    }, []);
    if (!modalContainer) return null;

    const variantStyle = VARIANT_STYLES[variant];

    const handleConfirm = () => {
        onConfirm?.();
        onClose();
    };

    const handleCancel = () => {
        onCancel?.();
        onClose();
    };

    return createPortal(
        <AnimatePresence mode="wait">
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[3000] flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="absolute inset-0 backdrop-blur-sm bg-color-base/40" onClick={handleCancel} />
                    <motion.div
                        className="relative z-10 bg-color-white-base rounded-2xl shadow-float-heavy w-[90%] max-w-md p-6 flex flex-col items-center gap-4"
                        initial={{ opacity: 0, scale: 0.92, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: 8 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IconAdaptive
                            Icon={variantStyle.icon}
                            baseSize={50}
                            strokeWidth={2}
                            className={variantStyle.iconColor}
                        />
                        <div className="flex flex-col items-center gap-1 text-center">
                            <p className="font-corp font-bold text-color-base text-base leading-snug">{message}</p>
                            {subMessage && (
                                <p className="font-notojp font-normal text-[12px] text-color-co-gray-dark leading-relaxed pt-2">
                                    {subMessage.split('\n').map((line, i) => (
                                        <span key={i}>
                                            {line}
                                            {i < subMessage.split('\n').length - 1 && <br />}
                                        </span>
                                    ))}
                                </p>
                            )}
                        </div>
                        <div className="flex gap-3 mt-2 w-full justify-center">
                            <div className="flex gap-3 mt-2 w-full justify-center">
                                {cancelLabel && (
                                    <ButtonGAdaptive
                                        label={cancelLabel}
                                        icon={X}
                                        colorType={2}
                                        size="xs"
                                        onClick={handleCancel}
                                    />
                                )}
                                {onConfirm && (
                                    <ButtonGAdaptive
                                        label={confirmLabel ?? '確認'}
                                        icon={Check}
                                        colorType={variantStyle.confirmColorType}
                                        size="xs"
                                        onClick={handleConfirm}
                                    />
                                )}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        modalContainer,
    );
}
